import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { AdminShell } from "./AdminShell";
import { api, formatApiErrorDetail } from "../../lib/api";

const StatusBadge = ({ published }) => (
  <span
    className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${
      published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
    }`}
  >
    {published ? "Published" : "Draft"}
  </span>
);

const DailyChart = ({ daily }) => (
  <div className="border border-slate-200 bg-white p-6" data-testid="admin-daily-chart">
    <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-slate-500">
      Daily Clicks — Last 30 Days
    </h3>
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="clickGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4687FF" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#4687FF" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            tickFormatter={(d) => d.slice(5)}
            interval={4}
            axisLine={false}
            tickLine={false}
          />
          <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{ border: "1px solid #e2e8f0", fontSize: 13, fontFamily: "Inter" }}
            labelStyle={{ fontWeight: 700, color: "#0F172A" }}
            formatter={(v) => [v, "clicks"]}
          />
          <Area type="monotone" dataKey="count" stroke="#4687FF" strokeWidth={2.5} fill="url(#clickGrad)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const SubscribersPanel = ({ data }) => {
  if (!data) return <p className="py-20 text-center text-sm text-slate-500">Loading subscribers…</p>;
  return (
    <div className="space-y-6" data-testid="admin-subscribers-panel">
      <div className="border border-slate-200 bg-white p-6">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Total Subscribers</p>
        <p className="mt-2 font-display text-3xl font-black tracking-tighter text-[#0F172A]">{data.count}</p>
      </div>
      {data.subscribers.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-500">
          No subscribers yet — the signup form is live in the footer and on the Deals page.
        </p>
      ) : (
        <div className="border border-slate-200 bg-white">
          {data.subscribers.map((s) => (
            <div key={s.email} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3.5">
              <span className="text-sm font-semibold text-[#0F172A]">{s.email}</span>
              <span className="text-xs text-slate-400">
                {String(s.created_at).slice(0, 10)} · via {s.source || "site"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BarList = ({ title, rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-slate-200 bg-white p-6">
      <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-slate-500">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400">No clicks recorded yet.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.key}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate font-semibold text-[#0F172A]">{r.key}</span>
                <span className="shrink-0 font-display font-bold text-[#4687FF]">{r.count}</span>
              </div>
              <div className="mt-1 h-1.5 w-full bg-slate-100">
                <div className="h-full bg-[#4687FF]" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AnalyticsPanel = ({ data }) => {
  if (!data) return <p className="py-20 text-center text-sm text-slate-500">Loading analytics…</p>;
  return (
    <div className="space-y-6" data-testid="admin-analytics-panel">
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Total CTA clicks", value: data.total },
          { label: "Last 7 days", value: data.last7 },
          { label: "Last 30 days", value: data.last30 },
        ].map((s) => (
          <div key={s.label} className="border border-slate-200 bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{s.label}</p>
            <p className="mt-2 font-display text-3xl font-black tracking-tighter text-[#0F172A]">{s.value}</p>
          </div>
        ))}
      </div>
      <DailyChart daily={data.daily || []} />
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <BarList title="Clicks by VPN" rows={data.by_vpn} />
        <BarList title="Top Placements" rows={data.by_placement} />
        <BarList title="Top Pages" rows={data.by_page} />
        <BarList title="Top Ad Keywords" rows={data.by_keyword || []} />
      </div>
      <p className="text-xs text-slate-400">
        Every affiliate button click on the site is recorded (VPN, button placement, page, and — for paid traffic —
        the Google Ads keyword via utm_term). Use this to see which content, CTAs and ad keywords drive your
        commissions.
      </p>
    </div>
  );
};

const TrafficPanel = ({ data }) => {
  if (!data) return <p className="py-20 text-center text-sm text-slate-500">Loading traffic…</p>;
  return (
    <div className="space-y-6" data-testid="admin-traffic-panel">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Total page views", value: data.total },
          { label: "Paid (Google Ads)", value: data.paid, color: "text-amber-600" },
          { label: "Organic / Direct", value: data.organic, color: "text-emerald-600" },
          { label: "Last 7 days", value: data.last7 },
        ].map((s) => (
          <div key={s.label} className="border border-slate-200 bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{s.label}</p>
            <p className={`mt-2 font-display text-3xl font-black tracking-tighter ${s.color || "text-[#0F172A]"}`}>{s.value}</p>
          </div>
        ))}
      </div>
      <div className="border border-slate-200 bg-white p-6" data-testid="admin-traffic-chart">
        <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-slate-500">
          Daily Views — Paid vs Organic (Last 30 Days)
        </h3>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="orgGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="paidGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} interval={4} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ border: "1px solid #e2e8f0", fontSize: 13, fontFamily: "Inter" }}
                labelStyle={{ fontWeight: 700, color: "#0F172A" }}
              />
              <Area type="monotone" dataKey="organic" name="Organic" stroke="#10b981" strokeWidth={2.5} fill="url(#orgGrad)" />
              <Area type="monotone" dataKey="paid" name="Paid" stroke="#f59e0b" strokeWidth={2.5} fill="url(#paidGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 flex gap-6 text-xs font-semibold">
          <span className="flex items-center gap-1.5 text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Organic / Direct</span>
          <span className="flex items-center gap-1.5 text-amber-600"><span className="h-2 w-2 rounded-full bg-amber-500" /> Paid (gclid / utm_medium=cpc)</span>
        </div>
      </div>
      <div className="border border-slate-200 bg-white p-6" data-testid="admin-traffic-pages">
        <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-slate-500">Top Pages by Source</h3>
        {data.top_pages.length === 0 ? (
          <p className="text-sm text-slate-400">No page views recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {data.top_pages.map((p) => (
              <div key={p.key} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 text-sm">
                <span className="truncate font-semibold text-[#0F172A]">{p.key}</span>
                <span className="flex shrink-0 gap-4 font-display text-xs font-bold">
                  <span className="text-amber-600">{p.paid} paid</span>
                  <span className="text-emerald-600">{p.organic} organic</span>
                  <span className="text-[#0F172A]">{p.total} total</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const KeywordsPanel = ({ data }) => {
  if (!data) return <p className="py-20 text-center text-sm text-slate-500">Loading keywords…</p>;
  return (
    <div className="space-y-6" data-testid="admin-keywords-panel">
      <div className="grid gap-4 sm:grid-cols-2">
        {[
          { label: "Paid page views (all time)", value: data.totals.paid_views },
          { label: "CTA clicks from ad keywords", value: data.totals.keyword_clicks },
        ].map((s) => (
          <div key={s.label} className="border border-slate-200 bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{s.label}</p>
            <p className="mt-2 font-display text-3xl font-black tracking-tighter text-[#0F172A]">{s.value}</p>
          </div>
        ))}
      </div>
      <div className="border border-slate-200 bg-white" data-testid="admin-keywords-table">
        <div className="border-b border-slate-200 p-6">
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.15em] text-slate-500">
            Keyword → Commission Performance
          </h3>
          <p className="mt-1.5 text-xs text-slate-400">
            Google Ads keywords (via utm_term) matched to affiliate CTA clicks — clicks are your commission events.
            High conversion = keyword worth more budget; zero-click keywords are burning spend.
          </p>
        </div>
        {data.keywords.length === 0 ? (
          <p className="p-10 text-center text-sm text-slate-500">
            No keyword traffic yet. Make sure your Google Ads final URL suffix includes utm_term={"{keyword}"}.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">
                  <th className="px-6 py-3">Keyword</th>
                  <th className="px-4 py-3">Views</th>
                  <th className="px-4 py-3">CTA Clicks</th>
                  <th className="px-4 py-3">Conv. Rate</th>
                  <th className="px-4 py-3">Clicks by VPN</th>
                  <th className="px-4 py-3">Last Click</th>
                </tr>
              </thead>
              <tbody>
                {data.keywords.map((k) => (
                  <tr key={k.keyword} className="border-b border-slate-100" data-testid={`keyword-row-${k.keyword.replace(/\s+/g, "-")}`}>
                    <td className="px-6 py-3.5 font-semibold text-[#0F172A]">
                      {k.keyword}
                      {k.campaigns.length > 0 && (
                        <span className="ml-2 text-[11px] font-normal text-slate-400">({k.campaigns.join(", ")})</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-slate-600">{k.views}</td>
                    <td className="px-4 py-3.5 font-display font-bold text-[#4687FF]">{k.clicks}</td>
                    <td className="px-4 py-3.5">
                      {k.conversion === null ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <span className={`font-bold ${k.conversion >= 10 ? "text-emerald-600" : k.conversion > 0 ? "text-amber-600" : "text-rose-500"}`}>
                          {k.conversion}%
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {k.by_vpn.length === 0 ? (
                        <span className="text-xs text-rose-500">no clicks</span>
                      ) : (
                        <span className="flex flex-wrap gap-1.5">
                          {k.by_vpn.map((v) => (
                            <span key={v.vpn} className="bg-[#4687FF]/10 px-2 py-0.5 text-[11px] font-bold text-[#2563eb]">
                              {v.vpn} ×{v.count}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-400">{k.last_click || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [news, setNews] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [keywords, setKeywords] = useState(null);
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, n] = await Promise.all([api.get("/admin/posts"), api.get("/admin/news")]);
      setPosts(p.data);
      setNews(n.data);
    } catch (e) {
      if (e.response?.status !== 401) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === "analytics") {
      api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    }
    if (tab === "traffic") {
      api.get("/admin/traffic").then((r) => setTraffic(r.data)).catch(() => {});
    }
    if (tab === "keywords") {
      api.get("/admin/keywords").then((r) => setKeywords(r.data)).catch(() => {});
    }
    if (tab === "subscribers") {
      api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {});
    }
  }, [tab]);

  const remove = async (type, item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/${type}/${type === "posts" ? item.post_id : item.news_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const togglePublish = async (type, item) => {
    const id = type === "posts" ? item.post_id : item.news_id;
    try {
      await api.put(`/admin/${type}/${id}`, { published: !item.published });
      toast.success(item.published ? "Moved to draft" : "Published");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const items = tab === "posts" ? posts : news;

  return (
    <AdminShell title="Content Dashboard">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2" data-testid="admin-tabs">
          {["posts", "news", "analytics", "traffic", "keywords", "subscribers"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-5 py-2.5 font-display text-sm font-bold capitalize transition-colors duration-150 ${
                tab === t ? "bg-[#0F172A] text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-[#4687FF]"
              }`}
            >
              {t === "posts"
                ? `Blog Posts (${posts.length})`
                : t === "news"
                ? `News (${news.length})`
                : t === "analytics"
                ? "CTA Analytics"
                : t === "traffic"
                ? "Traffic"
                : t === "keywords"
                ? "Keywords"
                : "Subscribers"}
            </button>
          ))}
        </div>
        {["posts", "news"].includes(tab) && (
        <Link
          to={tab === "posts" ? "/admin/posts/new" : "/admin/news/new"}
          data-testid="admin-new-btn"
          className="inline-flex items-center gap-2 bg-[#4687FF] px-5 py-2.5 font-display text-sm font-bold text-white transition-colors duration-200 hover:bg-[#2563eb]"
        >
          <Plus size={16} /> New {tab === "posts" ? "Post" : "News Item"}
        </Link>
        )}
      </div>

      {tab === "analytics" ? (
        <AnalyticsPanel data={analytics} />
      ) : tab === "traffic" ? (
        <TrafficPanel data={traffic} />
      ) : tab === "keywords" ? (
        <KeywordsPanel data={keywords} />
      ) : tab === "subscribers" ? (
        <SubscribersPanel data={subs} />
      ) : loading ? (
        <p className="py-20 text-center text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="py-20 text-center text-sm text-slate-500" data-testid="admin-empty-state">
          Nothing here yet — create your first {tab === "posts" ? "post" : "news item"}.
        </p>
      ) : (
        <div className="border border-slate-200 bg-white" data-testid={`admin-${tab}-list`}>
          {items.map((item) => {
            const id = tab === "posts" ? item.post_id : item.news_id;
            return (
              <div
                key={id}
                data-testid={`admin-row-${item.slug}`}
                className="flex flex-col gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-base font-bold tracking-tight text-[#0F172A]">{item.title}</h3>
                    <StatusBadge published={item.published} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.date} · {tab === "posts" ? item.category : item.tag} · /{tab === "posts" ? "blog" : "news"}/{item.slug}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => togglePublish(tab, item)}
                    data-testid={`admin-toggle-${item.slug}`}
                    className={`px-3.5 py-2 text-xs font-bold transition-colors duration-150 ${
                      item.published
                        ? "border border-slate-200 text-slate-600 hover:border-amber-400 hover:text-amber-600"
                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                    }`}
                  >
                    {item.published ? "Unpublish" : "Publish"}
                  </button>
                  <Link
                    to={`/admin/${tab}/${id}`}
                    data-testid={`admin-edit-${item.slug}`}
                    className="inline-flex items-center gap-1.5 border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:border-[#4687FF] hover:text-[#4687FF]"
                  >
                    <Pencil size={13} /> Edit
                  </Link>
                  <button
                    onClick={() => remove(tab, item)}
                    data-testid={`admin-delete-${item.slug}`}
                    className="inline-flex items-center gap-1.5 border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:border-rose-400 hover:text-rose-600"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
};

export default AdminDashboard;
